import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import {
  CheckCircle2,
  Crown,
  CreditCard,
  Calendar,
  AlertTriangle,
  Clock,
  Lock,
  Unlock,
  MessageCircle,
  BookOpen,
  Users,
  BarChart2,
  FileSpreadsheet,
} from "lucide-react";
import TopBar from "../components/TopBar";
import { useDistrict } from "@/lib/district-context";
import { useReadonly } from "../hooks/useReadonly";
import { supabase } from "../lib/supabase";
import { logger } from "@/lib/logger";

const PRO_FEATURES = [
  { icon: Users,           label: "Registrar y editar miembros y familias" },
  { icon: BookOpen,        label: "Registrar visitas, oraciones y bautismos" },
  { icon: BarChart2,       label: "Gestión completa de predicaciones y eventos" },
  { icon: FileSpreadsheet, label: "Exportar reportes a Excel y PDF" },
  { icon: Crown,           label: "Inteligencia pastoral (Kevia IA)" },
];

export function SettingsBillingContent() {
  const { district } = useDistrict();
  const {
    plan, isFreePlan, isInTrial, isTrialExpired,
    trialDaysLeft, isPastDue, subscriptionStatus, renewalAt,
    waUpgradeUrl,
  } = useReadonly();
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const renewalLabel = renewalAt
    ? new Date(renewalAt).toLocaleDateString("es-MX", {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  const handleManage = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("billing-portal", {
        body: { return_url: `${window.location.origin}/settings/billing` },
      });
      if (error) throw error;
      window.location.href = data.url;
    } catch (e) {
      logger.error("[SettingsBilling] error:", e);
      toast.error("No se pudo abrir el portal de gestión. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-5 space-y-5">

      {/* ── Status card ── */}
      {isInTrial && (
        <TrialCard
          trialDaysLeft={trialDaysLeft}
          waUpgradeUrl={waUpgradeUrl}
        />
      )}
      {isTrialExpired && isFreePlan && (
        <TrialExpiredCard waUpgradeUrl={waUpgradeUrl} />
      )}
      {!isFreePlan && (
        <ProPlanCard
          subscriptionStatus={subscriptionStatus}
          isPastDue={isPastDue}
          renewalLabel={renewalLabel}
          onManage={handleManage}
          waUpgradeUrl={waUpgradeUrl}
          loading={loading}
        />
      )}

      {/* ── Feature list ── */}
      <div className="card-soft overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/50">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Plan Pro incluye
          </p>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
          {PRO_FEATURES.map(({ icon: Icon, label }) => (
            <div key={label} className="px-4 py-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                <Icon size={15} className="text-[#1b4f9c] dark:text-blue-400" />
              </div>
              <p className="text-[13px] text-slate-700 dark:text-slate-300 flex-1">{label}</p>
              {!isFreePlan ? (
                <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
              ) : (
                <Lock size={13} className="text-slate-300 dark:text-slate-600 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Billing note ── */}
      <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center px-2 leading-relaxed">
        El cobro es mensual por distrito · $199 MXN/mes · Cancela cuando quieras
      </p>

    </div>
  );
}


// ── Trial active card ──────────────────────────────────────────────────────────

function TrialCard({ trialDaysLeft, waUpgradeUrl }) {
  const urgency = trialDaysLeft <= 3;

  return (
    <div className="space-y-3">
      {/* Trial status */}
      <div className="card-soft px-4 py-4 flex items-center gap-3">
        <div className={`h-11 w-11 rounded-[14px] flex items-center justify-center shrink-0 ${
          urgency ? "bg-amber-50 dark:bg-amber-900/20" : "bg-sky-50 dark:bg-sky-900/20"
        }`}>
          <Clock size={20} className={urgency ? "text-amber-500" : "text-sky-500 dark:text-sky-400"} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Prueba gratuita</p>
          <p className={`text-[11px] mt-0.5 ${urgency ? "text-amber-600 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"}`}>
            {trialDaysLeft === 1
              ? "Vence mañana"
              : `${trialDaysLeft} días restantes`}
          </p>
        </div>
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
          urgency
            ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
            : "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400"
        }`}>
          {urgency ? "POR VENCER" : "EN PRUEBA"}
        </span>
      </div>

      {/* Upgrade CTA */}
      <div className="bg-gradient-to-br from-[#0b3b82] via-[#1b4f9c] to-[#2563eb] rounded-[20px] p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white font-bold text-[17px] leading-tight">Activa Plan Pro</p>
            <p className="text-blue-200 text-[12px] mt-0.5">
              {urgency
                ? "Tu prueba vence pronto — no pierdas acceso"
                : "Gestión pastoral completa para tu distrito"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-white font-extrabold text-[22px] leading-none">$199</p>
            <p className="text-blue-200 text-[11px] mt-0.5">MXN / mes</p>
          </div>
        </div>
        <a
          href={waUpgradeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-11 rounded-[14px] bg-white text-[#1b4f9c] font-bold text-[14px] flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(0,0,0,0.2)] active:scale-[0.98] active:opacity-80 transition-all duration-200"
        >
          <MessageCircle size={16} />
          Contactar para activar Plan Pro
        </a>
        <p className="text-[11px] text-blue-200/70 text-center">
          El equipo de Kéves te contactará para completar tu suscripción
        </p>
      </div>
    </div>
  );
}


// ── Trial expired card ─────────────────────────────────────────────────────────

function TrialExpiredCard({ waUpgradeUrl }) {
  return (
    <div className="space-y-3">
      {/* Expired status */}
      <div className="card-soft px-4 py-4 flex items-center gap-3">
        <div className="h-11 w-11 rounded-[14px] bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
          <Lock size={20} className="text-red-500 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Prueba gratuita</p>
          <p className="text-[11px] text-red-500 dark:text-red-400 mt-0.5">Tu período de prueba terminó</p>
        </div>
        <span className="text-[10px] font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2.5 py-1 rounded-full">
          VENCIDA
        </span>
      </div>

      {/* Re-activate CTA */}
      <div className="bg-gradient-to-br from-[#0b3b82] via-[#1b4f9c] to-[#2563eb] rounded-[20px] p-5 space-y-4">
        <div>
          <p className="text-white font-bold text-[17px] leading-tight">Activa Plan Pro</p>
          <p className="text-blue-200 text-[13px] mt-1.5 leading-relaxed">
            Recupera acceso completo a tu historial, visitas, oraciones y todas las funciones de Kéves.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <p className="text-white font-extrabold text-[26px] leading-none">$199</p>
          <p className="text-blue-200 text-[12px] mb-1">MXN / mes</p>
        </div>
        <a
          href={waUpgradeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-11 rounded-[14px] bg-white text-[#1b4f9c] font-bold text-[14px] flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(0,0,0,0.2)] active:scale-[0.98] active:opacity-80 transition-all duration-200"
        >
          <MessageCircle size={16} />
          Contactar para activar Plan Pro
        </a>
        <p className="text-[11px] text-blue-200/70 text-center">
          El equipo de Kéves te contactará para completar tu suscripción
        </p>
      </div>
    </div>
  );
}


// ── Pro plan card ──────────────────────────────────────────────────────────────

function ProPlanCard({ subscriptionStatus, isPastDue, renewalLabel, onManage, waUpgradeUrl, loading }) {
  const isCancelled = subscriptionStatus === "cancelled" || subscriptionStatus === "suspended";

  return (
    <div className="space-y-3">
      {/* Status */}
      <div className="card-soft px-4 py-4 flex items-center gap-3">
        <div className={`h-11 w-11 rounded-[14px] flex items-center justify-center shrink-0 ${
          isCancelled ? "bg-slate-100 dark:bg-slate-700" :
          isPastDue   ? "bg-amber-50 dark:bg-amber-900/20" :
                        "bg-emerald-50 dark:bg-emerald-900/20"
        }`}>
          {isCancelled
            ? <Lock size={20} className="text-slate-400" />
            : isPastDue
              ? <AlertTriangle size={20} className="text-amber-500" />
              : <Crown size={20} className="text-emerald-600 dark:text-emerald-400" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Plan Pro</p>
          {renewalLabel && !isCancelled && (
            <div className="flex items-center gap-1 mt-0.5">
              <Calendar size={11} className="text-slate-400 shrink-0" />
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {isPastDue ? "Pago pendiente" : `Próximo cobro: ${renewalLabel}`}
              </p>
            </div>
          )}
          {isCancelled && (
            <p className="text-[11px] text-red-500 mt-0.5">Suscripción inactiva — solo lectura</p>
          )}
        </div>
        {!isCancelled && (
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
            isPastDue
              ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
          }`}>
            {isPastDue ? "PAGO PENDIENTE" : "ACTIVO"}
          </span>
        )}
      </div>

      {/* Past due warning */}
      {isPastDue && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-[16px] px-4 py-3.5 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-800 dark:text-amber-300 leading-relaxed">
            Tu método de pago requiere atención. Actualízalo para continuar con acceso completo.
          </p>
        </div>
      )}

      {/* Manage / reactivate button */}
      {isCancelled ? (
        <a
          href={waUpgradeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-11 rounded-[14px] bg-gradient-to-r from-[#1b4f9c] to-[#2563eb] text-white font-bold text-[14px] flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(27,79,156,0.3)] active:scale-[0.98] active:opacity-80 transition-all duration-200"
        >
          <MessageCircle size={16} />
          Reactivar Plan Pro
        </a>
      ) : (
        <button
          onClick={onManage}
          disabled={loading}
          className="w-full h-11 rounded-[14px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm active:scale-[0.98] active:opacity-80 transition-all duration-200 disabled:opacity-60"
        >
          <CreditCard size={16} className="text-slate-500 dark:text-slate-400" />
          {loading ? "Cargando…" : "Gestionar suscripción"}
        </button>
      )}
    </div>
  );
}


// ── Page wrapper ───────────────────────────────────────────────────────────────

export default function SettingsBilling() {
  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans">
      <TopBar title="Suscripción" safeFallback="/settings" />
      <SettingsBillingContent />
    </div>
  );
}
